import { SETTING_KEYS } from '../../types';
import { ok, reject } from '../result';
import type { Decision, SettingsOp, UpdateSettingCommand, UpdateSettingSnapshot } from './types';

/**
 * B spec §4.2, **F-5** — the settings writer (build unit B-4): the one interactive way a
 * setting changes. B's More tab writes the service credential, the theme, and the lock nudge
 * through this; the only other writer of the table is the archive import.
 *
 * ── "Every write deliberate", honored as two mechanisms ──────────────────────
 *
 * `repos/profile.ts` declined to offer a general setting writer because the table's one
 * interesting property — the closed key space behind §11.8's publish-or-deny archive
 * dispositions — "depends on every write being deliberate". F-5's ruling adds the writer as a
 * typed, key-validated command, which keeps that condition true rather than retiring it:
 *
 *   - **compile time** — `UpdateSettingCommand.key` is the closed `SettingKey` union, and the
 *     `putSetting` op carries a `SettingRow`, so no in-repo caller can spell a key
 *     `archive/fields.ts` has no disposition for;
 *   - **runtime** — this handler re-checks the key against `SETTING_KEYS`, because a key that
 *     crossed a UI boundary is a string whatever the annotation says (`ERR_SETTING_KEY`; the
 *     `rowSchemas` quarantine makes the same call for keys arriving in an archive).
 *
 * ── What is deliberately absent ──────────────────────────────────────────────
 *
 * **No state gate.** The window is any state (spec §4.2): settings are device/profile data with
 * no match owner — the `UpsertOurPlayer` argument, one table over.
 *
 * **No value vocabulary.** The row stores `value: unknown` and the engine's contract is the
 * closed *key* space; what a theme may be called or a nudge may number is the consuming
 * surface's business, exactly as it is for the import path writing these same rows.
 *
 * **The value never reaches a log line.** `backupCredential` travels through this handler, the
 * OBS-1 ring is persisted, and the ring is published in every `scope:'all'` archive — a logged
 * value here would be a credential in a shared file, undoing §11.8 from the other side. The log
 * carries the key and whether it replaced an existing row, nothing else.
 */

const MODULE = 'commands/settings';

/** The `SETTING_KEYS` vocabulary widened for a runtime membership test on an untrusted string. */
const KNOWN_KEYS: readonly string[] = SETTING_KEYS;

/**
 * B §4.2 (F-5) — write one setting, create-or-replace.
 *
 * | `cmd.key` | Outcome |
 * |---|---|
 * | in `SETTING_KEYS` | one `putSetting` op carrying `{key, value}` as sent |
 * | anything else | `ERR_SETTING_KEY`, no ops, nothing commits |
 */
export function handleUpdateSetting(
  cmd: UpdateSettingCommand,
  snap: UpdateSettingSnapshot,
): Decision<SettingsOp> {
  if (!KNOWN_KEYS.includes(cmd.key)) {
    return reject(
      MODULE,
      null,
      'ERR_SETTING_KEY',
      `"${String(cmd.key)}" is not a setting this app knows, so nothing was saved.`,
      { key: String(cmd.key) },
    );
  }

  return ok({
    ops: [{ t: 'putSetting', row: { key: cmd.key, value: cmd.value } }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: null,
        msg: snap.existing === undefined ? 'setting created' : 'setting updated',
        // The key only — never the value (see the module note: the ring is archived).
        data: { key: cmd.key },
      },
    ],
    post: [],
  });
}
